import { sendToApi } from 'api/client'
import { SUCCESS_RESPONSE_TYPE } from 'api/constants'
import { UseQueryOptions, useQuery } from 'react-query'
import { Batch } from 'types'
import { useEffect, useRef } from 'react'

interface Data extends Batch {}

export const fetchBatch = async ({ batchId, signal }: { batchId: string; signal?: AbortSignal }) => {
  const res = await sendToApi<Data>(`/batches/${batchId}`, { signal })
  if (res.type !== SUCCESS_RESPONSE_TYPE) {
    throw new Error(res.data)
  }
  return res.data
}

export const useBatch = (
  batchId?: string,
  options?: UseQueryOptions<Data> & {
    /**
     * Set to `true` to poll every 500 ms until the batch is complete.
     * Default is `false` (no polling).
     */
    pollUntilFinal?: boolean
    /**
     * Callback that fires when the batch is completed.
     * Only called once when the batch transitions from incomplete to complete.
     */
    onComplete?: () => void
  },
) => {
  const hasCompletedRef = useRef(false)

  const { pollUntilFinal = false, onComplete, ...queryOptions } = options ?? {}

  const pollingInterval = (data: Data | undefined) => {
    if (!pollUntilFinal) return false
    if (!data) return 500
    return data.is_complete ? false : 500
  }

  const query = useQuery<Data>(
    ['batches', batchId ?? null],
    () => {
      if (!batchId) throw new Error('No batchId provided')
      return fetchBatch({ batchId })
    },
    {
      refetchInterval: queryOptions.refetchInterval ?? pollingInterval,
      refetchOnWindowFocus: false,
      staleTime: (queryOptions.cacheTime || 0) * 0.5,
      enabled: !!batchId,
      ...queryOptions,
    },
  )

  // Handle the onComplete callback
  useEffect(() => {
    if (query.data?.is_complete && !hasCompletedRef.current && onComplete) {
      hasCompletedRef.current = true
      onComplete()
    }
  }, [query.data?.is_complete, onComplete])

  // Reset the completion flag when batchId changes
  useEffect(() => {
    hasCompletedRef.current = false
  }, [batchId])

  return query
}
